// Tone — one vocabulary shared by chips, banners and card accents, so "this
// is bad" looks the same everywhere it is said.
//
// These live apart from `primitives.tsx` for the same reason `navConfig.ts`
// lives apart from `Layout.tsx`: a module that exports both components and
// plain values loses React Fast Refresh for the whole file.

export type Tone = "neutral" | "ok" | "warn" | "danger" | "info" | "accent";

export const TONE_FG: Record<Tone, string> = {
  neutral: "var(--fg-dim)",
  ok: "var(--ok)",
  warn: "var(--warn)",
  danger: "var(--danger)",
  info: "var(--info)",
  accent: "var(--accent)",
};
export const TONE_BG: Record<Tone, string> = {
  neutral: "var(--neutral-bg)",
  ok: "var(--ok-bg)",
  warn: "var(--warn-bg)",
  danger: "var(--danger-bg)",
  info: "var(--info-bg)",
  accent: "var(--accent-bg)",
};
export const TONE_BORDER: Record<Tone, string> = {
  neutral: "var(--neutral-border)",
  ok: "var(--ok-border)",
  warn: "var(--warn-border)",
  danger: "var(--danger-border)",
  info: "var(--info-border)",
  accent: "var(--accent-border)",
};

export const toneColor = (t: Tone) => TONE_FG[t];

/** Maps the BE's status vocabularies onto tones in ONE place.
 *
 *  Every list and detail screen in the console renders a status string that
 *  the backend owns. Before this, each screen picked its own colour, so
 *  "blocked" was red on one page and amber on another. Unknown values fall to
 *  `neutral` rather than to a guess — a status this map has not been taught is
 *  exactly the case where a confident colour would be a lie. */
const STATUS_TONE: Record<string, Tone> = {
  // payables / payouts
  ready: "ok", available: "ok", paid: "ok", verified: "ok", approved: "ok",
  active: "ok", live: "ok", running: "ok", ok: "ok", complete: "ok", executed: "ok",
  pending: "warn", proposed: "warn", requested: "warn", submitted: "warn",
  awaiting_tx: "warn", processing: "warn", draft: "warn", review: "warn",
  blocked: "danger", failed: "danger", rejected: "danger", cancelled: "danger",
  expired: "danger", tripped: "danger", error: "danger", suspended: "danger",
  none: "neutral", unknown: "neutral", idle: "neutral", paused: "neutral",
};

export function statusTone(status: string | null | undefined): Tone {
  if (!status) return "neutral";
  return STATUS_TONE[status.toLowerCase().replace(/[\s-]+/g, "_")] ?? "neutral";
}
